import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { isSellerEntity, SELLER_ENTITY_LABELS_RU, isCrmCoreRole } from '@crm/shared'
import type { Request } from '../../../shared/api/types'
import { CategoryBadge, ProcRequestStatusBadge } from '../../../shared/ui/Badge'
import type { ProcRequestStatus } from '../../../shared/ui/Badge'
import { StatusEditor } from '../../clients/StatusEditor'
import { ResponsibleBuyerPicker } from './ResponsibleBuyerPicker'

type Props = {
  request: Request
  userRole: string | undefined
  canDeleteRequest: boolean
  isCopying: boolean
  isDeletingRequest: boolean
  isUnarchiving?: boolean
  onEdit: () => void
  onCopy: () => void
  onDelete: () => void
  onArchive?: () => void
  onUnarchive?: () => void
}

export function RequestHeader({
  request,
  userRole,
  canDeleteRequest,
  isCopying,
  isDeletingRequest,
  isUnarchiving = false,
  onEdit,
  onCopy,
  onDelete,
  onArchive,
  onUnarchive,
}: Props) {
  const queryClient = useQueryClient()

  // Видимость кнопок архива/возврата считаем по статусу заявки. Право (роль +
  // владение) уже отражено в canDeleteRequest (ADMIN/MANAGER/ROP/ASSISTANT), бэк
  // дополнительно проверяет ownership при самом запросе.
  // Заявку «В заказе» (CLOSED_WON) архивировать нельзя (временное правило
  // 10.06.2026 — иначе расходится аналитика win-rate/«в заказ»).
  const canArchiveRequest =
    canDeleteRequest && request.status !== 'ARCHIVED' && request.status !== 'CLOSED_WON'
  const canUnarchiveRequest = canDeleteRequest && request.status === 'ARCHIVED'

  const isOverdue =
    request.deadline &&
    new Date(request.deadline) < new Date() &&
    !['CLOSED_WON', 'CLOSED_LOST', 'ARCHIVED'].includes(request.status)

  return (
    <>
      {/* Breadcrumb */}
      <div className="text-sm text-gray-500 flex items-center gap-2">
        <Link to="/requests" className="hover:text-gray-900">
          Заявки
        </Link>
        <span>›</span>
        <span className="text-gray-900">
          {userRole === 'PROCUREMENT'
            ? `${request.client?.code ?? '—'} — ${request.requestNumber}`
            : (request.client?.name ?? '—')}
        </span>
      </div>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Имя клиента — нынешним ролям, кроме закупщика (белый список). */}
              {isCrmCoreRole(userRole ?? '') && userRole !== 'PROCUREMENT' ? (
                <>
                  <h1 className="text-lg font-semibold text-gray-900">
                    {request.client?.name ?? '—'}
                  </h1>
                  {request.client && <CategoryBadge category={request.client.category} />}
                </>
              ) : (
                <h1 className="text-lg font-semibold text-gray-900">
                  {request.client?.code ?? '—'} — {request.requestNumber}
                </h1>
              )}
              {userRole === 'PROCUREMENT' || userRole === 'ROS' ? (
                // Закупщику/РОЗ показываем ЗАКУПЩИЦКИЙ статус заявки (Новая /
                // Частично / Подготовка к КП / Готова к КП) — агрегат по позициям,
                // независимый от менеджерского request.status.
                <ProcRequestStatusBadge
                  status={(request.procurementStatus ?? 'NEW') as ProcRequestStatus}
                />
              ) : (
                <StatusEditor
                  requestId={request.id}
                  currentStatus={request.status}
                  isPartiallyProcessed={request.isPartiallyProcessed ?? false}
                  onApplied={() =>
                    void queryClient.invalidateQueries({ queryKey: ['requests', request.id] })
                  }
                />
              )}
            </div>
            <div className="text-sm text-gray-500">
              {request.type === 'TEKUCHKA'
                ? 'Текучка'
                : `Проект${request.projectName ? ` «${request.projectName}»` : ''}`}
              {' · '}
              <span className="text-gray-400">Менеджер:</span>{' '}
              {request.manager?.name || request.manager?.email || '—'}
              {/* Юрлицо продажи — у заявок до 23.09 не выбиралось, тогда не показываем. */}
              {isSellerEntity(request.sellerEntityId) && (
                <>
                  {' · '}
                  <span className="text-gray-400">Юрлицо:</span>{' '}
                  {SELLER_ENTITY_LABELS_RU[request.sellerEntityId]}
                </>
              )}
              {/* Надстройки проекта (Р12): закреплённый закупщик + ручной итог.
                  Закупщик назначается прямо здесь — до 04.09.2026 ручка
                  PATCH /requests/:id/responsible-buyer не вызывалась ни одним
                  компонентом, и фича была недостижима из интерфейса. */}
              {request.type === 'PROJECT' && (
                <ResponsibleBuyerPicker
                  requestId={request.id}
                  current={request.responsibleBuyer ?? null}
                  canEdit={
                    userRole !== undefined &&
                    ['ADMIN', 'MANAGER', 'ROP', 'ROS', 'PROCUREMENT', 'ASSISTANT'].includes(
                      userRole,
                    )
                  }
                />
              )}
              {request.type === 'PROJECT' && request.projectRealized != null && (
                <span
                  className={`ml-2 rounded-lg px-1.5 py-0.5 text-xs ${
                    request.projectRealized
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {request.projectRealized ? 'Реализован' : 'Не реализован'}
                </span>
              )}
              {request.sourceChannel && (
                <span className="text-gray-400 ml-2">· {request.sourceChannel}</span>
              )}
            </div>
          </div>

          <div className="text-sm text-right space-y-0.5">
            <div className={isOverdue ? 'text-red-600 font-semibold' : 'text-gray-600'}>
              {request.deadline
                ? `Дедлайн: ${new Date(request.deadline).toLocaleDateString('ru-RU')}${isOverdue ? ' ⚠' : ''}`
                : 'Без дедлайна'}
            </div>
            <div className="text-xs text-gray-400">
              Создана {new Date(request.createdAt).toLocaleDateString('ru-RU')}
            </div>
            {canDeleteRequest && (
              <div className="flex items-center gap-2 mt-1 justify-end flex-wrap">
                <button
                  onClick={onEdit}
                  className="text-xs text-brand-500 hover:text-brand-700 transition-colors"
                >
                  ✎ Изменить
                </button>
                <button
                  onClick={onCopy}
                  disabled={isCopying}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                >
                  {isCopying ? 'Копирование...' : '⎘ Копировать'}
                </button>
                {canArchiveRequest && onArchive && (
                  <button
                    onClick={onArchive}
                    className="text-xs text-amber-500 hover:text-amber-700 transition-colors"
                  >
                    📦 В архив
                  </button>
                )}
                {canUnarchiveRequest && onUnarchive && (
                  <button
                    onClick={onUnarchive}
                    disabled={isUnarchiving}
                    className="text-xs text-emerald-500 hover:text-emerald-700 transition-colors disabled:opacity-50"
                  >
                    {isUnarchiving ? 'Возврат…' : '↩ Вернуть из архива'}
                  </button>
                )}
                <button
                  onClick={onDelete}
                  disabled={isDeletingRequest}
                  className="text-xs text-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
                >
                  {isDeletingRequest ? 'Удаление...' : '🗑 Удалить'}
                </button>
              </div>
            )}
          </div>
        </div>

        {request.notes && (
          <p className="mt-3 text-sm text-gray-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            {request.notes}
          </p>
        )}

        {/* Причина отказа — заполняется при переходе в CLOSED_LOST. Показывается
            только если статус CLOSED_LOST и reason есть (при откате в QUOTE_SENT
            сервер обнуляет поле). Располагается ниже блока заметки менеджера. */}
        {request.status === 'CLOSED_LOST' && request.rejectionReason && (
          <div className="mt-2 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <div className="text-xs font-semibold text-red-700 mb-0.5">Причина отказа</div>
            <div className="text-gray-700 whitespace-pre-wrap">{request.rejectionReason}</div>
          </div>
        )}

        {/* Комментарий админа при ручной архивации заявки. */}
        {request.status === 'ARCHIVED' && request.archiveReason && (
          <div className="mt-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <div className="text-xs font-semibold text-amber-700 mb-0.5">В архиве</div>
            <div className="text-gray-700 whitespace-pre-wrap">{request.archiveReason}</div>
          </div>
        )}
      </div>
    </>
  )
}
